import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/redux/store';
import axios from 'axios';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { apiPath } from '@/redux/apiPath';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { userSchema } from '@/models/userSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { ThunkConfig } from '@/types/store';
import { CreateUserPayload, User, UserPrivilege } from './users.types';
import {
  USERS_FETCHING_ERROR_PREFIX,
  USER_CREATION_ERROR_PREFIX,
  USER_REMOVAL_ERROR_PREFIX,
  USER_PRIVILEGES_UPDATE_ERROR_PREFIX,
  USER_UPDATE_ERROR_PREFIX,
} from './users.constants';

// Async thunk to fetch users from the API
export const fetchUsers = createAsyncThunk<
  User[], // Return type of the payload creator
  void, // First argument to the payload creator (not needed here)
  { rejectValue: string; state: RootState } // Types for ThunkAPI
>('users/fetchUsers', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.get(apiPath.getAllUsers());

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, pageableSchema(userSchema));
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return (response.data as { content: User[] }).content;
  } catch (error: unknown) {
    let errorMessage = USERS_FETCHING_ERROR_PREFIX;
    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }
    return rejectWithValue(errorMessage);
  }
});

// Create user thunk
export const createUser = createAsyncThunk<
  User, // Return type
  CreateUserPayload, // Argument type
  { rejectValue: string; state: RootState }
>('users/createUser', async (userData, { rejectWithValue }) => {
  try {
    // Make the API call
    const response = await axiosInstanceNewAPI.post(apiPath.createUser(), {
      login: userData.login,
      name: userData.name,
      surname: userData.surname,
      password: userData.password,
      email: userData.email || null,
      orcidId: null,
      connectedToLdap: false,
      active: true,
      termsOfUseConsent: false,
    });

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return response.data as User;
  } catch (error: unknown) {
    let errorMessage = USER_CREATION_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});

// Remove user thunk
export const removeUser = createAsyncThunk<
  number, // Return type (user id)
  number, // Argument type (user id)
  { rejectValue: string; state: RootState }
>('users/removeUser', async (userId, { rejectWithValue }) => {
  try {
    await axiosInstanceNewAPI.delete(apiPath.removeUser(userId));
    return userId; // Return the user id to identify removed user in reducer
  } catch (error: unknown) {
    let errorMessage = USER_REMOVAL_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});

const privilegeKey = (privilege: UserPrivilege): string =>
  privilege.objectId ? `${privilege.privilegeType}:${privilege.objectId}` : privilege.privilegeType;

// Managed "role" privileges: always explicitly granted or revoked to match the desired state below
const MANAGED_PRIVILEGE_KEYS = ['IS_ADMIN', 'IS_CURATOR', 'READ_PROJECT:*'];

export const grantPrivilege = createAsyncThunk<
  User,
  { userId: number; privilege: UserPrivilege },
  ThunkConfig
>('users/grantPrivilege', async ({ userId, privilege }, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.post<User>(apiPath.grantUserPrivilege(userId), {
      privilegeType: privilege.privilegeType,
      objectId: privilege.objectId,
    });

    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid privilege data received from server');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to grant privilege');
  }
});

export const revokePrivilege = createAsyncThunk<
  User,
  { userId: number; privilege: UserPrivilege },
  ThunkConfig
>('users/revokePrivilege', async ({ userId, privilege }, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.post<User>(apiPath.revokeUserPrivilege(userId), {
      privilegeType: privilege.privilegeType,
      objectId: privilege.objectId,
    });

    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid privilege data received from server');
    }

    return response.data;
  } catch (error) {
    return rejectWithValue('Failed to revoke privilege');
  }
});

// Update user privileges thunk
export const updateUserPrivileges = createAsyncThunk<
  User, // Return type
  { user: User; privileges: UserPrivilege[]; isAdmin?: boolean; isCurator?: boolean }, // Argument type
  { rejectValue: string; state: RootState }
>(
  'users/updateUserPrivileges',
  async (
    { user, privileges, isAdmin = false, isCurator = false },
    { dispatch, rejectWithValue },
  ) => {
    try {
      // Desired state of the managed role privileges (admin/curator/default project read access)
      const desiredPrivilegesRaw: UserPrivilege[] = [];
      if (isAdmin) desiredPrivilegesRaw.push({ privilegeType: 'IS_ADMIN', objectId: null });
      if (isCurator) desiredPrivilegesRaw.push({ privilegeType: 'IS_CURATOR', objectId: null });
      desiredPrivilegesRaw.push(...privileges);

      const desiredPrivilegesByKey = new Map(
        desiredPrivilegesRaw.map(privilege => [privilegeKey(privilege), privilege]),
      );
      const desiredPrivileges = Array.from(desiredPrivilegesByKey.values());
      const desiredKeys = new Set(desiredPrivilegesByKey.keys());
      const currentPrivileges = user.privileges;
      const currentKeys = new Set(currentPrivileges.map(privilegeKey));

      const toGrant = desiredPrivileges.filter(
        privilege => !currentKeys.has(privilegeKey(privilege)),
      );
      const toRevoke = currentPrivileges.filter(
        privilege =>
          MANAGED_PRIVILEGE_KEYS.includes(privilegeKey(privilege)) &&
          !desiredKeys.has(privilegeKey(privilege)),
      );

      let latestUser: User = user;

      // eslint-disable-next-line no-restricted-syntax
      for (const privilege of toRevoke) {
        // eslint-disable-next-line no-await-in-loop
        const revokeResult = await dispatch(revokePrivilege({ userId: user.id, privilege }));
        if (revokePrivilege.rejected.match(revokeResult)) {
          throw new Error(revokeResult.payload || 'Failed to revoke privilege');
        }
        latestUser = revokeResult.payload;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const privilege of toGrant) {
        // eslint-disable-next-line no-await-in-loop
        const grantResult = await dispatch(grantPrivilege({ userId: user.id, privilege }));
        if (grantPrivilege.rejected.match(grantResult)) {
          throw new Error(grantResult.payload || 'Failed to grant privilege');
        }
        latestUser = grantResult.payload;
      }

      // Validate response
      const isDataValid = validateDataUsingZodSchema(latestUser, userSchema);
      if (!isDataValid) {
        throw new Error('Invalid user data received from server');
      }

      return latestUser;
    } catch (error: unknown) {
      let errorMessage = USER_PRIVILEGES_UPDATE_ERROR_PREFIX;

      if (error instanceof Error) {
        if (axios.isAxiosError(error)) {
          errorMessage += extractAxiosErrorMessage(error);
        } else {
          errorMessage += error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage += error;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

// Update user thunk
export const updateUser = createAsyncThunk<
  User, // Return type
  User, // Argument type (full, merged user object)
  { rejectValue: string; state: RootState }
>('users/updateUser', async (user, { rejectWithValue }) => {
  try {
    // Make the API call
    const response = await axiosInstanceNewAPI.put(apiPath.updateUser(user.id), {
      login: user.login,
      name: user.name,
      surname: user.surname,
      email: user.email ?? '',
      orcidId: user.orcidId,
      connectedToLdap: user.connectedToLdap,
      active: user.active,
      termsOfUseConsent: user.termsOfUseConsent,
      ...(user.password ? { password: user.password } : {}),
    });

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return response.data as User;
  } catch (error: unknown) {
    let errorMessage = USER_UPDATE_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});
